/**
 * External API Configuration
 * Compatibility layer - routes through runtime config
 */

import { getApiBaseUrl, isConfigLoaded, loadRuntimeConfig } from '@/config/runtime-config';
import { logger } from '@/services/logger.service';

// Get base URL from runtime config
export const PROXY_BASE = isConfigLoaded() ? getApiBaseUrl() : '';

// Token storage keys (for user data only - tokens are in httpOnly cookies)
export const TOKEN_KEYS = {
  ACCESS_TOKEN: 'sp_ext_access_token', // Not used with httpOnly cookies
  REFRESH_TOKEN: 'sp_ext_refresh_token', // Not used with httpOnly cookies
  ID_TOKEN: 'sp_ext_id_token', // Not used with httpOnly cookies
  TOKEN_EXPIRY: 'sp_ext_token_expiry', // Not used with httpOnly cookies
  USER_DATA: 'sp_ext_user_data',
} as const;

// User groups from Cognito mapped to internal roles
// CRITICAL: Must match EXACT Cognito group names (case-sensitive)
// Cognito groups: admin, agent, seller, marketing, external-agent
export const GROUP_TO_ROLE: Record<string, string> = {
  admin: 'admin',
  agent: 'agent',  // Fixed: was 'agents' (plural)
  seller: 'seller',  // Fixed: was 'sellers' (plural)
  'external-agent': 'external-agent',  // Fixed: was 'external_agents' (underscore)
  marketing: 'marketing',
  'deal-coordinator': 'deal-coordinator',
} as const;

export const ROLE_TO_GROUP: Record<string, string> = {
  admin: 'admin',
  agent: 'agent',  // Fixed: was 'agents' (plural)
  seller: 'seller',  // Fixed: was 'sellers' (plural)
  'external-agent': 'external-agent',  // Fixed: was 'external_agents' (underscore)
  marketing: 'marketing',
  'deal-coordinator': 'deal-coordinator',
} as const;

/**
 * Fetch wrapper that uses runtime config for base URL
 * All API calls are logged via the API client interceptors
 */
export async function apiFetch(url: string, options: RequestInit = {}): Promise<Response> {
  // Ensure config is loaded
  if (!isConfigLoaded()) {
    await loadRuntimeConfig();
  }

  const baseUrl = getApiBaseUrl();
  const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`;

  logger.debug(`apiFetch: ${options.method || 'GET'} ${url}`, {
    fullUrl,
    hasBody: !!options.body,
  });

  return fetch(fullUrl, {
    ...options,
    credentials: 'omit', // JWT Bearer token auth (no cookies) — matches axios withCredentials: false
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
}

// Re-export for compatibility
export const API_BASE = PROXY_BASE;
